// src/context/NotificationContext.js
//
// A centered popup notification — replaces window.alert() and inline
// "Saved!" text across the app. Dims the background and stays up until
// the person taps the button or clicks outside it (no auto-dismiss timer),
// so it's impossible to miss. One at a time: a new notify() call while one
// is already showing just replaces it.
import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from "react";
import { CheckCircle2, XCircle, Info, AlertTriangle } from "lucide-react";

const NotificationContext = createContext(null);

export function useNotify() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotify must be used within a NotificationProvider");
  return ctx;
}

const STYLES = {
  success: { Icon: CheckCircle2, color: "var(--green)" },
  error:   { Icon: XCircle,      color: "var(--red)" },
  info:    { Icon: Info,         color: "var(--accent)" },
  warning: { Icon: AlertTriangle,color: "var(--amber)" },
};

export function NotificationProvider({ children }) {
  const [item, setItem] = useState(null); // { title, message, type }
  const [toasts, setToasts] = useState([]);  // small corner notices that clear themselves
  const toastId = useRef(0);

  const dismiss = useCallback(() => setItem(null), []);

  const show = useCallback((message, opts = {}) => {
    setItem({ message, title: opts.title, type: opts.type || "success" });
  }, []);

  // A small notice in the corner that disappears on its own and never blocks
  // the screen. For things that are just FYI (e.g. "screenshot captured") —
  // the centered popup above is for things that need an answer.
  const toast = useCallback((message, opts = {}) => {
    const id = ++toastId.current;
    setToasts(t => [...t.slice(-2), { id, message, title: opts.title, type: opts.type || "info" }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), opts.duration || 4000);
  }, []);

  const api = useMemo(() => ({
    success: (message, opts) => show(message, { ...opts, type: "success" }),
    error:   (message, opts) => show(message, { ...opts, type: "error" }),
    info:    (message, opts) => show(message, { ...opts, type: "info" }),
    warning: (message, opts) => show(message, { ...opts, type: "warning" }),
    toast,
    dismiss,
  }), [show, dismiss, toast]);

  const { Icon, color } = STYLES[item?.type] || STYLES.success;

  return (
    <NotificationContext.Provider value={api}>
      {children}

      <div aria-live="polite" style={{ position: "fixed", right: 18, bottom: 18, zIndex: 1100, display: "flex", flexDirection: "column", gap: 8, pointerEvents: "none" }}>
        {toasts.map(t => {
          const st = STYLES[t.type] || STYLES.info;
          return (
            <div key={t.id} role="status" style={{
              display: "flex", alignItems: "center", gap: 10, padding: "11px 16px", minWidth: 220, maxWidth: 320,
              background: "var(--surface)", border: "1px solid var(--border)", borderLeft: `3px solid ${st.color}`,
              borderRadius: "var(--radius)", boxShadow: "var(--shadow-lg)", animation: "fadeIn .2s ease", pointerEvents: "auto",
            }}>
              <st.Icon size={17} color={st.color} style={{ flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                {t.title && <p style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>{t.title}</p>}
                {t.message && <p style={{ fontSize: 12.5, color: "var(--text-2)", lineHeight: 1.45 }}>{t.message}</p>}
              </div>
            </div>
          );
        })}
      </div>

      {item && (
        <div
          onClick={dismiss}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, animation: "fadeIn .15s ease",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-lg)",
              padding: "30px 28px", maxWidth: 360, width: "100%",
              textAlign: "center",
            }}
          >
            <div style={{
              width: 52, height: 52, borderRadius: "50%", margin: "0 auto 16px",
              background: "var(--surface-2)", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Icon size={26} color={color} />
            </div>
            {item.title && <p style={{ fontSize: 16, fontWeight: 700, color: "var(--text-1)", marginBottom: 6 }}>{item.title}</p>}
            {item.message && <p style={{ fontSize: 13.5, color: "var(--text-2)", lineHeight: 1.55 }}>{item.message}</p>}
            <button
              onClick={dismiss}
              style={{
                marginTop: 20, padding: "10px 28px", borderRadius: "var(--radius)",
                border: "none", background: "var(--accent)", color: "#fff",
                fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-body)",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
}
